import { useState } from 'react';
import { venueAddressOneLine } from '@bre-ticketing/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { ApiError, VenueRow } from '../api';
import { endpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';

/**
 * Venues (Phase 2).
 *
 * A venue is the long-lived thing; its seating layouts are manifests, versioned
 * separately, which is why this page links through to them rather than trying
 * to show a room's shape here.
 */

/** Offered as a shortcut; any IANA zone is accepted and validated server-side. */
const COMMON_TIMEZONES = [
  'America/Chicago',
  'America/New_York',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
];

const slugify = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

export default function VenuesPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const venues = useQuery<{ venues: VenueRow[] }, ApiError>({ queryKey: ['venues'], queryFn: endpoints.venues });
  // Dev tools exist only outside production; the API refuses them there
  // regardless, this just keeps the button off a production screen.
  const health = useQuery({ queryKey: ['health'], queryFn: endpoints.health, staleTime: 5 * 60_000 });
  const [seedResult, setSeedResult] = useState<string | null>(null);
  const seed = useMutation({
    mutationFn: () => endpoints.seedVenues(),
    onSuccess: (result) => {
      setError(null);
      setSeedResult(
        result.seeded.length > 0
          ? `Seeded ${result.seeded.map((v) => `${v.name} (${v.counts.seats} seats)`).join(', ')}.`
          : `Nothing to do: ${result.skipped.join(', ')} already exist.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
    onError: (cause: ApiError) => setError(cause.message),
  });
  const devTools = health.data && health.data.app_environment !== 'production' && can('dev_tools.use');

  if (!can('events.view')) return <NotPermitted permission="events.view" />;

  const rows = venues.data?.venues ?? [];

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {seedResult ? <p className="rounded border border-ok/30 bg-ok-soft px-3 py-2 text-sm text-ok">{seedResult}</p> : null}

      {adding ? (
        <NewVenue
          onCancel={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            setError(null);
            void queryClient.invalidateQueries({ queryKey: ['venues'] });
          }}
          onError={(cause) => setError(cause.message)}
        />
      ) : null}

      <Panel
        title="Venues"
        description="Rooms you sell into. Seating layouts live in manifests, versioned per venue."
        actions={
          <div className="flex gap-2">
            {devTools ? (
              <Button onClick={() => seed.mutate()} disabled={seed.isPending} title="Dev only: Memorial Auditorium, a GA hall and a dinner-show room">
                {seed.isPending ? 'Seeding…' : 'Seed sample venues'}
              </Button>
            ) : null}
            {can('venues.manage') && !adding ? (
              <Button variant="primary" onClick={() => setAdding(true)}>
                Add a venue
              </Button>
            ) : null}
          </div>
        }
      >
        <Table headers={['Venue', 'Timezone', 'Where', 'Status', 'Added']}>
          {venues.isPending ? (
            <EmptyRow columns={5}>Loading…</EmptyRow>
          ) : rows.length === 0 ? (
            <EmptyRow columns={5}>No venues yet. Add one, then draw its seating as a manifest.</EmptyRow>
          ) : (
            rows.map((venue) => (
              <tr key={venue.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3">
                  <Link
                    to={`/venues/${venue.id}`}
                    className="font-medium text-accent underline-offset-2 hover:underline"
                  >
                    {venue.name}
                  </Link>
                  <div className="text-xs text-ink-400">{venue.slug}</div>
                </td>
                <td className="px-5 py-3 text-sm text-ink-600">{venue.timezone}</td>
                <td className="px-5 py-3 text-sm text-ink-600">
                  {venueAddressOneLine(venue) || <span className="text-bad">no address</span>}
                </td>
                <td className="px-5 py-3">
                  {venue.active === 1 ? <Badge tone="ok">ACTIVE</Badge> : <Badge tone="neutral">INACTIVE</Badge>}
                </td>
                <td className="tabular px-5 py-3 text-xs text-ink-400">{formatWhen(venue.created_at)}</td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}

function NewVenue({
  onCancel,
  onCreated,
  onError,
}: {
  onCancel: () => void;
  onCreated: () => void;
  onError: (error: ApiError) => void;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [timezone, setTimezone] = useState(COMMON_TIMEZONES[0] as string);
  const [line1, setLine1] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [postal, setPostal] = useState('');

  const create = useMutation({
    mutationFn: () =>
      endpoints.createVenue({
          name: name.trim(),
          slug: slug.trim() || slugify(name),
          timezone,
          // The street goes in here rather than being remembered later: it
          // prints on every ticket and email for this venue (D-150).
          address_line1: line1.trim() || null,
          city: city.trim() || null,
          region: region.trim() || null,
          postal_code: postal.trim() || null,
        }),
    onSuccess: onCreated,
    onError,
  });

  const effectiveSlug = slug.trim() || slugify(name);

  return (
    <Panel title="Add a venue" description="The timezone decides which day a sale belongs to, so get it right. The street address prints on every ticket and email for this venue.">
      <form
        className="flex flex-wrap items-end gap-4 px-5 py-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <Field label="Name" htmlFor="venue-name">
          <input
            id="venue-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Memorial Auditorium"
            className="w-64 rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </Field>

        <Field label="Slug" htmlFor="venue-slug" hint={effectiveSlug ? `URLs will use ${effectiveSlug}` : undefined}>
          <input
            id="venue-slug"
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            placeholder={slugify(name) || 'memorial-auditorium'}
            className="w-56 rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </Field>

        <Field label="Timezone" htmlFor="venue-tz">
          <input
            id="venue-tz"
            list="tz-options"
            value={timezone}
            onChange={(event) => setTimezone(event.target.value)}
            className="w-52 rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
          <datalist id="tz-options">
            {COMMON_TIMEZONES.map((zone) => (
              <option key={zone} value={zone} />
            ))}
          </datalist>
        </Field>

        <Field label="Street" htmlFor="venue-line1">
          <input
            id="venue-line1"
            value={line1}
            onChange={(event) => setLine1(event.target.value)}
            placeholder="200 N Front St"
            className="w-56 rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </Field>

        <Field label="City" htmlFor="venue-city">
          <input
            id="venue-city"
            value={city}
            onChange={(event) => setCity(event.target.value)}
            className="w-40 rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </Field>

        <Field label="State" htmlFor="venue-region">
          <input
            id="venue-region"
            value={region}
            onChange={(event) => setRegion(event.target.value)}
            className="w-20 rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </Field>

        <Field label="ZIP" htmlFor="venue-postal">
          <input
            id="venue-postal"
            value={postal}
            onChange={(event) => setPostal(event.target.value)}
            className="w-24 rounded border border-line bg-surface px-2 py-1.5 text-sm"
          />
        </Field>

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={name.trim().length === 0 || create.isPending}>
            {create.isPending ? 'Adding…' : 'Add venue'}
          </Button>
          <Button type="button" onClick={onCancel} disabled={create.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Panel>
  );
}
